import { useAtomValue } from 'jotai';
import { Stack, Avatar, Typography } from '@mui/material';
import {
  DeviceThermostat,
  WbTwilight,
  Battery4Bar,
  Opacity,
  BatteryCharging90,
  BatteryChargingFull,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { selectedDeviceSafeAtom } from '@/components/common/MapView';
import { tempUnitAtom } from '@/interfaces/utils';
import { getTemperatureText } from '@/components/common/utils';

interface StatusCard {
  name: string;
  value: number | string;
  unit: string;
  color: string;
  icon: JSX.Element;
}

export default function DeviceDetailStatusCards() {
  const device = useAtomValue(selectedDeviceSafeAtom);
  const tempUnit = useAtomValue(tempUnitAtom);

  const { t } = useTranslation();

  // TODO: Fetch alarm count from API
  const cards: StatusCard[] = [
    {
      name: t('Devices.label.temperature'),
      value: device.data.temperature ? getTemperatureText(device.data.temperature, tempUnit) : '--',
      unit: tempUnit === 'CELSIUS' ? '℃' : '℉',
      color: '#4285F4',
      icon: <DeviceThermostat />,
    },
    { name: t('Devices.label.alarm'), value: 0, unit: '', color: '#EA4335', icon: <WbTwilight /> },
  ];

  if (device.isHumiditySupported) {
    cards.splice(1, 0, {
      name: t('Devices.label.humidity'),
      value: device.data.humid,
      unit: '%',
      color: '#2D3748',
      icon: <Opacity />,
    });
  }

  const batteryCard: StatusCard = {
    name: t('Devices.label.battery'),
    value: '',
    unit: '',
    color: '',
    icon: null,
  };
  switch (device.data.chargeState) {
    case 1:
      Object.assign(batteryCard, {
        value: t('Devices.label.charging'),
        unit: '',
        color: '#0FA958',
        icon: <BatteryCharging90 sx={{ rotate: '90deg' }} />,
      });
      break;
    case 2:
      Object.assign(batteryCard, {
        value: t('Devices.label.charged'),
        unit: '',
        color: '#0FA958',
        icon: <BatteryChargingFull sx={{ rotate: '90deg' }} />,
      });
      break;
    case 0:
    default:
      Object.assign(batteryCard, {
        value: device.data.battery ?? '--',
        unit: '%',
        color: '#2D3748',
        icon: <Battery4Bar sx={{ rotate: '90deg' }} />,
      });
  }
  cards.push(batteryCard);

  return (
    <Stack direction="row" gap={1}>
      {cards.map((status) => (
        <Stack
          key={status.name}
          direction="row"
          gap={2}
          alignItems="center"
          sx={{ flex: '1 1 0', bgcolor: 'white', p: 1, boxShadow: 1, borderRadius: '12px' }}
        >
          <Avatar sx={{ bgcolor: status.color }}>{status.icon}</Avatar>
          <Stack>
            <Typography variant="h5" fontWeight="bold">
              {status.value}
              <Typography variant="subtitle1" component="span">
                {status.unit}
              </Typography>
            </Typography>
            <Typography variant="caption" fontWeight="medium">
              {status.name}
            </Typography>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}
